import { useState } from "react";
import { FileText, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import MedicalProfileSheet from "@/components/health/MedicalProfileSheet";
import {
  deriveExemptionStatus,
  EXEMPTION_STATUS_META,
  getHealthExemptionDocumentUrl,
  reviewHealthExemption,
} from "@/lib/healthOperations";

export default function AdminHealthOperationsPanel({ child, profile, exemptions, onChanged }) {
  const [profileOpen, setProfileOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [reviewDraft, setReviewDraft] = useState(null);

  if (!child) return null;
  const review = async (row) => {
    const status = reviewDraft?.status;
    if (!status || reviewDraft?.id !== row.id) return;
    const label = status === "approved" ? "Approve" : status === "rejected" ? "Decline" : "Revoke";
    setBusyId(row.id);
    try {
      await reviewHealthExemption({ exemptionId: row.id, status, note: reviewDraft.note });
      toast.success(`Exemption ${label.toLowerCase()}d`);
      setReviewDraft(null);
      await onChanged?.();
    } catch (error) {
      toast.error(error.message || `Could not ${label.toLowerCase()} the exemption`);
    } finally {
      setBusyId(null);
    }
  };
  const openDocument = async (path) => {
    const url = await getHealthExemptionDocumentUrl(path);
    if (url) window.open(url, "_blank", "noopener");
    else toast.error("Document not available");
  };

  return (
    <>
      <section className="mt-5 rounded-2xl border border-neutral-200 bg-neutral-50/40 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">Protected care details</p>
            <p className="mt-0.5 text-[11px] text-neutral-600">Conditions, physician, and insurance remain restricted to family and center administrators.</p>
          </div>
          <button type="button" data-testid={`admin-child-health-profile-${child.id}`} onClick={() => setProfileOpen(true)} className="rounded-full border border-primary/40 bg-white px-3 py-1 text-[11px] font-semibold text-primary-hover hover:bg-primary/5">
            {profile ? "Update" : "Add details"}
          </button>
        </div>
      </section>

      <section className="mt-5">
        <div className="flex items-center gap-2">
          <ShieldCheck size={15} weight="duotone" className="text-primary-hover" />
          <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">Exemption requests · {exemptions.length}</p>
        </div>
        {exemptions.length === 0 ? (
          <p className="mt-2 rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-3 text-center text-[13px] text-neutral-500">No exemption requests on file.</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {exemptions.map((row) => {
              const status = deriveExemptionStatus(row);
              const meta = EXEMPTION_STATUS_META[status] || EXEMPTION_STATUS_META.submitted;
              const busy = busyId === row.id;
              const draft = reviewDraft?.id === row.id ? reviewDraft : null;
              const actionLabel = draft?.status === "approved" ? "Approve" : draft?.status === "rejected" ? "Decline" : "Revoke";
              return (
                <li key={row.id} data-testid={`admin-child-health-exemption-${row.id}`} data-status={status} className="rounded-2xl border border-neutral-200 bg-white p-3">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-neutral-900">{row.record_type?.label || "Health requirement"}</p>
                      <p className="text-[11px] text-neutral-500">Submitted {String(row.submitted_at || "").slice(0, 10) || "recently"}{row.expires_on ? ` · expires ${row.expires_on}` : ""}</p>
                      <p className="mt-1 whitespace-pre-wrap text-[12px] leading-5 text-neutral-700"><strong>Basis:</strong> {row.basis}</p>
                      {row.notes && <p className="mt-1 whitespace-pre-wrap text-[12px] leading-5 text-neutral-600">Family note: {row.notes}</p>}
                      {row.review_note && <p className="mt-1 text-[11px] text-neutral-600">Review note: {row.review_note}</p>}
                    </div>
                    <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.tone}`}>{meta.label}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {row.document_path && <button type="button" onClick={() => openDocument(row.document_path)} className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50"><FileText size={11} weight="duotone" />Open document</button>}
                    {row.status === "submitted" && <>
                      <button type="button" disabled={busy} onClick={() => setReviewDraft({ id: row.id, status: "approved", note: "" })} className="rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-primary-hover disabled:opacity-60">Approve</button>
                      <button type="button" disabled={busy} onClick={() => setReviewDraft({ id: row.id, status: "rejected", note: "" })} className="rounded-full border border-[#C6851E]/40 bg-white px-2.5 py-1 text-[11px] font-semibold text-[#8A5A12] hover:bg-[#C6851E]/10 disabled:opacity-60">Decline</button>
                    </>}
                    {row.status === "approved" && <button type="button" disabled={busy} onClick={() => setReviewDraft({ id: row.id, status: "revoked", note: "" })} className="rounded-full border border-[#C6851E]/40 bg-white px-2.5 py-1 text-[11px] font-semibold text-[#8A5A12] hover:bg-[#C6851E]/10 disabled:opacity-60">Revoke</button>}
                  </div>
                  {draft && (
                    <form
                      className="mt-3 rounded-xl border border-primary/20 bg-primary/5 p-3"
                      onSubmit={(event) => { event.preventDefault(); review(row); }}
                      data-testid={`admin-child-health-exemption-review-${row.id}`}
                    >
                      <p className="text-xs font-semibold text-neutral-800">{actionLabel} this exemption</p>
                      <label className="mt-2 block">
                        <span className="text-[11px] font-medium text-neutral-600">Note to the family <span className="font-normal text-neutral-400">(optional)</span></span>
                        <textarea
                          autoFocus
                          value={draft.note}
                          onChange={(event) => setReviewDraft((current) => current?.id === row.id ? { ...current, note: event.target.value } : current)}
                          rows={2}
                          className="mt-1 w-full rounded-lg border border-neutral-200 bg-white px-2.5 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                        />
                      </label>
                      <div className="mt-2 flex justify-end gap-2">
                        <button type="button" disabled={busy} onClick={() => setReviewDraft(null)} className="rounded-full px-3 py-1 text-[11px] font-semibold text-neutral-600 hover:bg-white disabled:opacity-60">Cancel</button>
                        <button type="submit" disabled={busy} className="rounded-full bg-primary px-3 py-1 text-[11px] font-semibold text-white hover:bg-primary-hover disabled:opacity-60">{busy ? "Saving…" : `Confirm ${actionLabel.toLowerCase()}`}</button>
                      </div>
                    </form>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <MedicalProfileSheet
        open={profileOpen}
        onOpenChange={setProfileOpen}
        child={child}
        profile={profile}
        onSaved={onChanged}
      />
    </>
  );
}
